/**
 * Мітки продуктивності для Playwright і RUM (performance-budget.md, розд. 3 і 3.1):
 * однаково працюють у Chromium і WebKit. Назви — PERF_MARKS; пари start → end рахує тест.
 */
export const PERF_MARKS = {
  listReady: 'cl:list-ready',
  todayReady: 'cl:today-ready',
  cardOpenStart: 'cl:card-open-start',
  cardReady: 'cl:card-ready',
  callStart: 'cl:call-start',
  callSheetReady: 'cl:call-sheet-ready',
  saveStart: 'cl:save-start',
  saveDone: 'cl:save-done',
  /** Реєстр моделі (ADM-101): перший рядок списку сутностей з оцінкою кількості записів. */
  adminModelReady: 'cl:admin-model-ready',
  /**
   * Клік по пункту «Сутності й поля» в меню адмінки: початок заміру ADM-101 «≤ 1 с від кліку»
   * (`cl:admin-model-start` → `cl:admin-model-ready`). Стоїть на `timeStamp` кліку.
   */
  adminModelStart: 'cl:admin-model-start',
} as const;

export type PerfMark = (typeof PERF_MARKS)[keyof typeof PERF_MARKS];

export function mark(name: PerfMark) {
  performance.mark(name);
}

/**
 * Мітка на `timeStamp` події (клік, клавіша): замір «від кліку» враховує й обробку самої події,
 * а не лише час після неї (як `palette:start`, performance-budget.md, розд. 3.2).
 */
export function markAt(name: PerfMark, at: number) {
  try {
    performance.mark(name, { startTime: at });
  } catch {
    performance.mark(name);
  }
}

/**
 * Мітка після того, як кадр з новим станом намальовано: rAF спрацьовує перед малюванням,
 * повідомлення MessageChannel — уже після нього. «Дані на екрані», а не «дані в стані».
 */
export function markAfterPaint(name: PerfMark) {
  requestAnimationFrame(() => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => performance.mark(name);
    channel.port2.postMessage(0);
  });
}

/** Перша поява позначки за сесію сторінки (напр., «список готовий» — лише раз). */
const once = new Set<PerfMark>();
export function markOnceAfterPaint(name: PerfMark) {
  if (once.has(name)) return;
  once.add(name);
  markAfterPaint(name);
}
